import Component from '@glimmer/component';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { LinkTo } from '@ember/routing';
import { on } from '@ember/modifier';
import { VStack, Section } from 'tio-ui/components/layout';
import { Heading, Text } from 'tio-ui/components/typography';
import Button from 'tio-reimagined/components/button';
import PageHero from 'tio-reimagined/components/page-hero';
import { t, type IntlService } from 'ember-intl';
import { htmlSafe } from '@ember/template';
import Chip from 'tio-reimagined/components/chip';
import { formatShortDate } from 'tio-reimagined/format';
import {
  PhSparkle,
  PhArrowRight,
  PhCaretDown,
  PhCaretUp,
  PhRocket,
  PhArchive,
} from 'ember-phosphor-icons';
import DataIndicator from 'tio-employee/components/reimagined/data-indicator';
import type { DataSource } from 'tio-employee/components/reimagined/data-indicator';
import StatePill from 'tio-employee/components/reimagined/tas/state-pill';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import { isActiveInstanceState } from 'tio-common/utils/tuition-assistance/application-states';
import type {
  DataSourceInfo,
  EligibleTemplate,
  LiveTasData,
  LiveTasProgram,
} from 'tio-employee/services/reimagined-data';

interface TasProgramsModel {
  tasData: LiveTasData;
  dataSourceInfo: DataSourceInfo;
  eligibleTemplates: EligibleTemplate[];
}

interface TasProgramsSignature {
  Args: {
    model: TasProgramsModel;
  };
}

export default class CareerPathwaysTasProgramsIndex extends Component<TasProgramsSignature> {
  @service declare intl: IntlService;
  @service declare reimaginedData: ReimaginedDataService;

  @tracked showHistory = false;

  get isDemoMode() {
    return this.reimaginedData.isDemoMode;
  }

  get tasSource(): DataSource {
    if (this.isDemoMode) return 'demo';
    return this.args.model.tasData.isLiveData ? 'live' : 'placeholder';
  }

  get allPrograms(): LiveTasProgram[] {
    return this.args.model.tasData.programs;
  }

  get allowedTypes(): Set<string> {
    return new Set(['TAS.ProgramType.1', 'TAS.ProgramType.4.2', 'TAS.ProgramType.4.3']);
  }

  get programs(): LiveTasProgram[] {
    return this.allPrograms.filter(
      (p) =>
        isActiveInstanceState(p.state) &&
        (p.typeClassification ? this.allowedTypes.has(p.typeClassification) : false)
    );
  }

  get historyPrograms(): LiveTasProgram[] {
    return this.allPrograms.filter(
      (p) =>
        !isActiveInstanceState(p.state) &&
        (p.typeClassification ? this.allowedTypes.has(p.typeClassification) : false)
    );
  }

  get hasActive() {
    return this.programs.length > 0;
  }

  get hasHistory() {
    return this.historyPrograms.length > 0;
  }

  toggleHistory = () => {
    this.showHistory = !this.showHistory;
  };

  get eligibleTemplates(): EligibleTemplate[] {
    return this.args.model.eligibleTemplates ?? [];
  }

  <template>
    <VStack class="gap-6">
      <PageHero
        @gradientClass="bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-500"
        @rounded="2xl"
        class="shadow-lg"
      >
        <:default>
          <div class="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div class="max-w-2xl">
              <div
                class="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-white/80"
              >
                <PhSparkle class="h-4 w-4" />
                {{t "tuition_assistance.reimagined.programs.hero_eyebrow"}}
              </div>
              <Heading @level={{1}} class="text-white">
                {{t "tuition_assistance.reimagined.programs.hero_title"}}
              </Heading>
              <Text class="text-white/80">
                {{t "tuition_assistance.reimagined.programs.hero_subtitle"}}
              </Text>
            </div>
            <LinkTo @route="authenticated.tas.dashboard">
              <Button @intent="primary">{{t
                  "tuition_assistance.reimagined.programs.cta_start"
                }}</Button>
            </LinkTo>
          </div>
        </:default>
      </PageHero>

      <DataIndicator @source={{this.tasSource}} @showBadge={{true}} @dataSourceId="tas-programs">
        <Section class="mb-6">
          <:header>
            <div class="flex items-center gap-2">
              <PhRocket @size={{20}} class="text-teal-600" />
              <Heading @level={{2}}>{{t
                  "tuition_assistance.reimagined.programs.section_active"
                }}</Heading>
              {{#if this.hasActive}}
                <span
                  class="ml-2 rounded-full bg-teal-100 px-2.5 py-0.5 text-xs font-semibold text-teal-700 dark:bg-teal-900 dark:text-teal-200"
                >
                  {{this.programs.length}}
                </span>
              {{/if}}
            </div>
          </:header>
          <:body>
            {{#if this.hasActive}}
              <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
                {{#each this.programs as |program|}}
                  <div class="rounded-2xl ring-1 ring-gray-950/[0.04] bg-white p-5 shadow-sm">
                    <div class="flex items-start justify-between gap-4">
                      <div>
                        <Heading @level={{3}}>{{program.name}}</Heading>
                        <Text @intent="muted">{{program.institution}}</Text>
                        {{#if program.startDate}}
                          <Text @intent="muted" class="block whitespace-nowrap">
                            {{t
                              "tuition_assistance.reimagined.programs.label_starts"
                              date=(formatShortDate program.startDate)
                            }}
                          </Text>
                        {{/if}}
                      </div>
                      <StatePill @state={{program.state}} />
                    </div>
                    <div class="mt-4 flex items-center justify-between">
                      {{#if program.major}}
                        <Text @intent="muted">
                          {{t
                            "tuition_assistance.reimagined.programs.label_major"
                            major=program.major
                          }}
                        </Text>
                      {{else}}
                        <span></span>
                      {{/if}}
                      <LinkTo
                        @route="authenticated.tas.programs.show"
                        @model={{program.id}}
                        class="tio-copy text-sm font-semibold"
                      >
                        {{t "tuition_assistance.reimagined.programs.link_open"}}
                        <PhArrowRight class="ml-1 inline h-4 w-4" />
                      </LinkTo>
                    </div>
                  </div>
                {{/each}}
              </div>
            {{else}}
              <div
                class="rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-8 text-center"
              >
                <PhRocket @size={{32}} class="mx-auto mb-3 text-gray-400" />
                <Heading @level={{3}}>{{t
                    "tuition_assistance.reimagined.programs.empty_title"
                  }}</Heading>
                <Text @intent="muted">
                  {{t "tuition_assistance.reimagined.programs.empty_body"}}
                </Text>
                <div class="mt-4">
                  <LinkTo @route="authenticated.tas.dashboard">
                    <Button @intent="primary">{{t
                        "tuition_assistance.reimagined.programs.empty_cta"
                      }}</Button>
                  </LinkTo>
                </div>
              </div>
            {{/if}}
          </:body>
        </Section>

        {{! History Section }}
        {{#if this.hasHistory}}
          <Section>
            <:header>
              <Heading @level={{2}}>
                <button
                  type="button"
                  class="flex w-full items-center gap-2 text-left"
                  {{on "click" this.toggleHistory}}
                >
                  <PhArchive @size={{20}} class="text-gray-500" />
                  <span>
                    {{t "tuition_assistance.reimagined.programs_index.section_history"}}
                  </span>
                  <span
                    class="ml-2 rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-semibold text-gray-600"
                  >
                    {{this.historyPrograms.length}}
                  </span>
                  <span class="ml-auto">
                    {{#if this.showHistory}}
                      <PhCaretUp @size={{16}} class="text-gray-500" />
                    {{else}}
                      <PhCaretDown @size={{16}} class="text-gray-500" />
                    {{/if}}
                  </span>
                </button>
              </Heading>
            </:header>
            <:body>
              {{#if this.showHistory}}
                <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
                  {{#each this.historyPrograms as |program|}}
                    <div
                      class="rounded-2xl border border-gray-200 bg-gray-50/50 p-5 dark:bg-gray-800/50"
                    >
                      <div class="flex items-start justify-between gap-4">
                        <div>
                          <Heading @level={{3}}>{{program.name}}</Heading>
                          <Text @intent="muted">{{program.institution}}</Text>
                          {{#if program.startDate}}
                            <Text @intent="muted" class="block whitespace-nowrap">
                              {{t
                                "tuition_assistance.reimagined.programs.label_starts"
                                date=(formatShortDate program.startDate)
                              }}
                            </Text>
                          {{/if}}
                        </div>
                        <StatePill @state={{program.state}} />
                      </div>
                      <div class="mt-4 flex items-center justify-between">
                        {{#if program.major}}
                          <Text @intent="muted">
                            {{t
                              "tuition_assistance.reimagined.programs.label_major"
                              major=program.major
                            }}
                          </Text>
                        {{else}}
                          <span></span>
                        {{/if}}
                        <LinkTo
                          @route="authenticated.tas.programs.show"
                          @model={{program.id}}
                          class="inline-flex items-center gap-1 text-sm font-semibold text-gray-600 hover:text-gray-800"
                        >
                          {{t "tuition_assistance.reimagined.programs.link_open"}}
                          <PhArrowRight class="h-4 w-4" />
                        </LinkTo>
                      </div>
                    </div>
                  {{/each}}
                </div>
              {{/if}}
            </:body>
          </Section>
        {{/if}}
      </DataIndicator>

      {{! Eligible Benefits Section }}
      {{#if this.eligibleTemplates.length}}
        <DataIndicator
          @source={{this.tasSource}}
          @showBadge={{true}}
          @dataSourceId="tas-eligible-benefits"
        >
          <Section>
            <:header>
              <Heading @level={{2}}>{{t "tas.dashboard.eligible_benefits"}}</Heading>
            </:header>
            <:body>
              <div class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                {{#each this.eligibleTemplates as |template|}}
                  <div
                    class="rounded-2xl ring-1 ring-gray-950/4 bg-white p-5 shadow-sm hover:shadow-md transition-shadow flex flex-col gap-4"
                  >
                    <div class="flex justify-end gap-2 min-h-5">
                      {{#if template.isEnrolled}}
                        <Chip @appearance="faded" @intent="success" @size="sm">
                          {{t "tas.dashboard.chips.enrolled"}}
                        </Chip>
                      {{/if}}
                      {{#if template.isDirectPayProgram}}
                        <Chip @appearance="faded" @intent="primary" @size="sm">
                          {{htmlSafe (t "common.tas.course_cash.default")}}
                        </Chip>
                      {{/if}}
                    </div>
                    <div class="flex flex-col gap-1.5">
                      <Heading @level={{3}}>{{template.name}}</Heading>
                      {{#if template.description}}
                        <Text @intent="muted" class="line-clamp-2">
                          {{template.description}}
                        </Text>
                      {{/if}}
                    </div>
                    <div>
                      <LinkTo
                        @route="authenticated.tas.getting-started"
                        @model={{template.id}}
                        class="tio-copy text-sm font-semibold"
                      >
                        {{t "tas.dashboard.see_more"}}
                        <PhArrowRight class="ml-1 inline h-4 w-4" />
                      </LinkTo>
                    </div>
                  </div>
                {{/each}}
              </div>
            </:body>
          </Section>
        </DataIndicator>
      {{/if}}
    </VStack>
  </template>
}
